import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { API_URL } from './adminUtils';
import {
  Globe,
  Save,
  Loader2,
  Eye,
} from 'lucide-react';

const SEOSettingsManagement = () => {
  const [settings, setSettings] = useState({
    og_title: '',
    og_description: '',
    og_image: '',
    og_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/og`);
      setSettings(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings/og`, settings);
      toast.success('SEO settings saved successfully');
    } catch (error) {
      toast.error('Failed to save SEO settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-bold">SEO / Open Graph Settings</h1>
        <p className="text-muted-foreground">Manage how your site appears when shared on social media</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-cameroon-green" />
            Open Graph Meta Tags
          </CardTitle>
          <CardDescription>
            These settings control how your site appears when shared on Facebook, Twitter, LinkedIn, etc.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Site Title</Label>
            <Input
              value={settings.og_title || ''}
              onChange={(e) => setSettings({ ...settings, og_title: e.target.value })}
              placeholder="CCA Montreal - Council of Cultural Associations"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Site Description</Label>
            <Textarea
              value={settings.og_description || ''}
              onChange={(e) => setSettings({ ...settings, og_description: e.target.value })}
              placeholder="The Council of Cameroon Cultural Associations in Montreal..."
              rows={3}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Site URL</Label>
            <Input
              value={settings.og_url || ''}
              onChange={(e) => setSettings({ ...settings, og_url: e.target.value })}
              placeholder="https://cca-montreal.com"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Social Share Image URL</Label>
            <Input
              value={settings.og_image || ''}
              onChange={(e) => setSettings({ ...settings, og_image: e.target.value })}
              placeholder="https://..."
            />
            <p className="text-xs text-muted-foreground">Recommended size: 1200x630 pixels</p>
            {settings.og_image && (
              <div className="mt-2 aspect-video max-w-md rounded-lg overflow-hidden bg-muted">
                <img src={settings.og_image} alt="OG Preview" className="w-full h-full object-cover" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Preview Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Social Media Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden max-w-md bg-white">
            {settings.og_image && (
              <div className="aspect-video bg-muted">
                <img src={settings.og_image} alt="Preview" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-3">
              <p className="text-xs text-gray-500 uppercase">{settings.og_url || 'cca-montreal.com'}</p>
              <p className="font-semibold text-gray-900 line-clamp-2">{settings.og_title || 'CCA Montreal'}</p>
              <p className="text-sm text-gray-500 line-clamp-2">{settings.og_description || 'Site description...'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="bg-cameroon-green hover:bg-cameroon-green/90">
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Saving...' : 'Save SEO Settings'}
      </Button>
    </div>
  );
};

export default SEOSettingsManagement;
